/**
 * Tiny toast / flash-message store.
 *
 * Why in-house instead of `sonner` / `react-hot-toast`?
 *   - We already have zustand for state, so the store is ~30 lines.
 *   - We control aria semantics (role="status" for info / "alert" for error).
 *   - One less dep to keep updated, and zero runtime CSS.
 *
 * Usage:
 *
 *   import { toast } from "@/lib/toast";
 *
 *   toast.success("Deal created.");
 *   toast.error("Could not save — try again.");
 *   toast.info("Redirected to existing deal.", {
 *     action: { label: "Open in new tab", onClick: () => ... },
 *   });
 *
 * Toasts auto-dismiss after `duration` ms (default 5s for info / success,
 * 8s for error). Pass `duration: 0` to make them sticky until the user
 * dismisses them.
 */

import { create } from "zustand";

export type ToastKind = "info" | "success" | "warning" | "error";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
  /** Optional secondary line (e.g. detail text under the headline). */
  description?: string;
  /** Optional action button (e.g. "Open the existing deal"). */
  action?: ToastAction;
  /** ms; 0 = sticky. */
  duration: number;
  createdAt: number;
}

interface ToastStore {
  toasts: Toast[];
  push: (t: Omit<Toast, "id" | "createdAt">) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const useToastStore = create<ToastStore>((set, get) => ({
  toasts: [],
  push: (t) => {
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const toast: Toast = { ...t, id, createdAt: Date.now() };
    set((s) => ({ toasts: [...s.toasts, toast] }));
    if (toast.duration > 0) {
      setTimeout(() => {
        get().dismiss(id);
      }, toast.duration);
    }
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

export const useToasts = () => useToastStore((s) => s.toasts);
export const useDismissToast = () => useToastStore((s) => s.dismiss);

interface PushOpts {
  description?: string;
  action?: ToastAction;
  /** Override default duration (ms). 0 = sticky. */
  duration?: number;
}

const defaultDuration = (kind: ToastKind): number =>
  kind === "error" ? 8000 : kind === "warning" ? 6000 : 5000;

const make =
  (kind: ToastKind) =>
  (message: string, opts: PushOpts = {}): string => {
    const t: Omit<Toast, "id" | "createdAt"> = {
      kind,
      message,
      duration: opts.duration ?? defaultDuration(kind),
    };
    if (opts.description !== undefined) t.description = opts.description;
    if (opts.action !== undefined) t.action = opts.action;
    return useToastStore.getState().push(t);
  };

export const toast = {
  info: make("info"),
  success: make("success"),
  warning: make("warning"),
  error: make("error"),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
  clear: () => useToastStore.getState().clear(),
};
